import { message } from 'antd';
import md5 from 'blueimp-md5';
import jwtDecode from 'jwt-decode';
import _ from 'lodash';
import moment from 'moment';
import { history } from 'umi';

import { bindServices } from '@/services/api';

const api = bindServices('external');
import { formFieldBehaviorsMapping } from '@/utils/core/business-logic';
import { isJsonLike, parseJson, translate } from '@/utils/infrastructure';
import { getToken } from '@/utils/infrastructure/communication';
import { CoreEnums } from '@/utils/domain/constants';
import { authProcess, externalHandle, getProcessConfig, sendProcessForm } from './service';
import PropTypes from 'prop-types';

const initialState = {
  done: false,
  config: null,
  processId: null,
  validationErrors: [],
  telegramAuthorize: null,
};

const readStoredJwt = (raw) => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed?.accessToken) return parsed.accessToken;
  } catch (e) {}
  return raw;
};

const persistExternalSession = (code, res) => {
  const sessionToken = _.isPlainObject(res?.newToken)
    ? res.newToken
    : _.isPlainObject(res?.token)
    ? res.token
    : null;
  if (!code || !sessionToken?.accessToken) return;

  const keyId = md5(code);
  localStorage.setItem(
    `x-auth-externalToken"${keyId}"`,
    JSON.stringify({ ...sessionToken, issuedAt: Date.now() }),
  );

  const processId = _.get(res, 'process.id');
  if (processId) localStorage.setItem(`x-external-processId"${keyId}"`, processId);
  if (res.processInstance) {
    localStorage.setItem(
      `x-external-processInstance"${keyId}"`,
      JSON.stringify(res.processInstance),
    );
  }
};

const Model = {
  namespace: 'external',
  state: initialState,
  effects: {
    *authProcess({ payload }, { call, put, select }) {
      const token = getToken();

      const publicCode = _.get(history.location, 'query.publicCode');
      const keyId = payload.code ?? publicCode;
      const pKeyId = `x-${payload.type}-processId"${keyId}"`;
      let pId = localStorage.getItem(pKeyId);

      const { newToken, process, processInstance } = yield select((state) => state.external);

      try {
        if (!token) {
          // The external session is created by GET /external/handle. /auth/externalProcess was removed.
          let auth = null;
          if (newToken) {
            auth = { token: newToken, process, processInstance };
          } else if (payload.type !== 'external') {
            auth = yield call(authProcess, payload);
          }

          if (auth?.token) {
            localStorage.setItem(
              `x-auth-${payload.type}Token"${keyId}"`,
              JSON.stringify({ ...auth.token, issuedAt: Date.now() }),
            );
            localStorage.setItem(pKeyId, _.get(auth.process, 'id'));

            if (auth.processInstance) {
              localStorage.setItem(
                `x-${payload.type}-processInstance"${keyId}"`,
                JSON.stringify(auth.processInstance),
              );
            }

            pId = _.get(auth.process, 'id');
          } else if (
            payload.type === 'external' &&
            !localStorage.getItem(`x-auth-externalToken"${keyId}"`)
          ) {
            return;
          }
        }

        const res = payload.shouldInit ? yield call(getProcessConfig, pId) : null;

        yield put({
          type: 'queryProcessConfig',
          payload: {
            ...(res && { res }),
            code: keyId,
            pId,
            type: payload.type,
          },
        });
      } catch (e) {}
    },

    *sendProcessForm({ payload }, { call, put }) {
      try {
        const code = _.get(history.location, 'query.publicCode');
        const params = {
          ...payload,
          processId: localStorage.getItem(`x-public-processId"${code}"`),
        };
        const res = yield call(sendProcessForm, params);
        yield put({ type: 'queryProcessFormSent', payload: res });
      } catch (e) {
        if (e?.validationErrors) {
          yield put({
            type: 'queryFormSentError',
            payload: e.validationErrors,
          });
        }
      }
    },

    *handle({ payload }, { call, put }) {
      try {
        // The email code is the credential. Sending the logged-in JWT makes
        // /external/handle reject the click. If a session exists, the request
        // is opened afterwards inside /applications so that session stays.
        const session = getToken({ global: true });
        const keepSession = !!session?.accessToken;
        const res = yield call(externalHandle, payload);
        if (!keepSession) persistExternalSession(payload.code, res);
        yield put({ type: 'queryExternalHandle', payload: { ...res, keepSession } });
      } catch (err) {
        if (err && err.code !== '301') {
          let message = isJsonLike(err.message) ? JSON.parse(err.message)?.Message : err.message;
          if (!message && err?.clone) {
            try {
              const e = yield err.clone().json();
              const bm = parseJson(e.message);
              message = bm?.Message ?? e.message ?? e.Message;
            } catch {}
          }
          yield put({ type: 'global/withError', payload: message });
          history.replace(`/external/400`);
        }
      }
    },

    *checkTelegramAuthorize({ payload }, { call, put }) {
      try {
        yield call(api.getById, { route: 'providers/telegram/authorize', ...payload });
        yield put({ type: 'queryTelegramAuthorize', payload: true });
      } catch (e) {
        if (e?.status === 401) {
          message.error(translate('form.telegram.unauthorized'), 5);
          history.replace('/');
        }
      }
    },

    *telegramAuthorize({ payload }, { call, put }) {
      try {
        yield call(api.create, { route: `providers/telegram/authorize`, data: payload });
        message.success(translate('form.telegram.message'), 5);
        history.replace('/');
      } catch (e) {}
    },

    *cleanup({ payload }, { put }) {
      yield put({ type: 'reset', payload });
    },
  },
  reducers: {
    queryProcessConfig(state, { payload }) {
      const jwt = readStoredJwt(
        localStorage.getItem(`x-auth-${payload.type}Token"${payload.code}"`),
      );
      if (!jwt) return state;

      let user;
      try {
        user = jwtDecode(jwt);
      } catch (e) {
        return state;
      }

      moment.locale(user.language || 'es-pe');
      moment.tz.setDefault(user.timeZone);

      return {
        ...state,
        config: {
          ...payload.res,
          processId: payload.pId,
          ...(payload.res && {
            behaviorCases: formFieldBehaviorsMapping(payload.res),
          }),
        },
      };
    },

    queryProcessFormSent(state, { payload }) {
      return {
        ...initialState,
        done: payload?.processInstance || {},
      };
    },

    queryFormSentError(state, { payload }) {
      return {
        ...state,
        validationErrors: payload,
      };
    },

    queryExternalHandle(state, { payload }) {
      const { command, redirectRoute, autoFire, keepSession, ...data } = payload;

      if (autoFire && command && command.command !== CoreEnums.RequestDetailCommandType.Details) {
        //  set temporally variable to persist the auto command execution
        sessionStorage.setItem('x-autoFireCommand', JSON.stringify(command));
      }

      const instanceId = data?.processInstance?.id || data?.processInstanceId;
      const redirectTo = keepSession && instanceId ? `/applications/${instanceId}` : redirectRoute;

      return {
        ...state,
        ...data,
        redirectTo,
      };
    },

    queryTelegramAuthorize(state, { payload }) {
      return {
        ...state,
        telegramAuthorize: payload,
      };
    },

    reset() {
      return initialState;
    },
  },
};

export default Model;
